import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

/** Maps each sortable column key to a function returning its comparable value. */
export type SortAccessors<T, K extends string> = Record<
  K,
  (row: T) => string | number | null | undefined
>;

/**
 * Pure, stable-ish sort by a single accessor. Nullish values always sort last,
 * regardless of direction, so empty cells don't jump to the top on desc.
 *
 * @param rows - Rows to sort (not mutated).
 * @param accessor - Returns the comparable value for a row.
 * @param direction - Ascending or descending.
 * @returns A new sorted array.
 */
export function sortRows<T>(
  rows: readonly T[],
  accessor: (row: T) => string | number | null | undefined,
  direction: SortDirection,
): T[] {
  const factor = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

/**
 * Client-side column sorting for hand-rolled tables. Clicking a column sorts by
 * it ascending; clicking the active column flips the direction.
 *
 * @param rows - The (already filtered) rows to sort.
 * @param accessors - Per-column comparable-value accessors. Memoize this so the
 *   sort only recomputes when the data or sort state changes.
 * @param initial - The initial column + direction.
 * @returns The current sort state, a toggle handler, and the sorted rows.
 */
export function useTableSort<T, K extends string>(
  rows: readonly T[],
  accessors: SortAccessors<T, K>,
  initial: SortState<K>,
) {
  const [sort, setSort] = useState<SortState<K>>(initial);

  const toggleSort = (key: K) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  };

  const sorted = useMemo(
    () => sortRows(rows, accessors[sort.key], sort.direction),
    [rows, accessors, sort],
  );

  return { sort, toggleSort, sorted };
}
